import * as THREE from 'three';

/**
 * Renderer + frame loop.
 *
 * Two scenes are rendered every frame: the world, and a separate viewmodel scene
 * drawn on top with its own narrow-FOV camera. This is how every modern shooter
 * avoids the weapon clipping through walls and lets the gun use a different FOV
 * from the world — you cannot get both from a single camera.
 *
 * Post-processing runs on the combined result so the weapon receives the same
 * grade, bloom and grain as the world.
 */

const PHASES = ['input', 'sim', 'ai', 'fx', 'ui'];
/** Phases that keep running while paused — enough for the menu, not the world. */
const PAUSED_PHASES = ['ui'];

export class Engine {
  constructor(canvas, quality) {
    this.canvas = canvas;
    this.quality = quality;

    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: false, // SMAA in the post stack instead; cheaper with MRT-style passes
      powerPreference: 'high-performance',
      stencil: false,
      depth: true,
      alpha: false,
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, quality.maxPixelRatio));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.autoClear = false;
    this.renderer.info.autoReset = false;

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(80, window.innerWidth / window.innerHeight, 0.05, 600);

    // Viewmodel pass: near-plane pulled in tight so the gun never clips the camera.
    this.viewScene = new THREE.Scene();
    this.viewCamera = new THREE.PerspectiveCamera(60, this.camera.aspect, 0.01, 12);

    this.clock = new THREE.Clock();
    this.elapsed = 0;
    this.frame = 0;
    this.paused = false;

    this.updates = new Map(PHASES.map((p) => [p, []]));
    this.composer = null; // set by PostFX
    this._raf = null;
    this._stats = { fps: 60, ms: 16.7, _acc: 0, _n: 0 };

    window.addEventListener('resize', () => this.resize());
  }

  registerUpdate(fn, phase = 'sim') {
    if (!this.updates.has(phase)) throw new Error(`[engine] bad phase "${phase}"`);
    this.updates.get(phase).push(fn);
    return () => {
      const list = this.updates.get(phase);
      const i = list.indexOf(fn);
      if (i !== -1) list.splice(i, 1);
    };
  }

  resize() {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, this.quality.maxPixelRatio));
    this.renderer.setSize(w, h);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.viewCamera.aspect = w / h;
    this.viewCamera.updateProjectionMatrix();
    this.composer?.setSize(w, h);
  }

  start() {
    const loop = () => {
      this._raf = requestAnimationFrame(loop);
      this.tick();
    };
    this._raf = requestAnimationFrame(loop);
  }

  stop() {
    if (this._raf) cancelAnimationFrame(this._raf);
    this._raf = null;
  }

  tick() {
    // Clamp dt so a stalled tab (or a breakpoint) can't teleport actors through walls.
    const dt = Math.min(this.clock.getDelta(), 1 / 20);
    this.frame++;

    // While paused we still run the 'ui' phase and still render, so the menu
    // animates over a frozen world. Simulation time does not advance, which keeps
    // cooldowns, recoil recovery and AI timers from draining behind the menu.
    const phases = this.paused ? PAUSED_PHASES : PHASES;
    if (!this.paused) this.elapsed += dt;

    for (const phase of phases) {
      for (const fn of this.updates.get(phase)) {
        try {
          fn(dt, this.elapsed);
        } catch (err) {
          console.error(`[engine] update error in "${phase}"`, err);
        }
      }
    }

    this.render();

    this._stats._acc += dt;
    this._stats._n++;
    if (this._stats._acc >= 0.5) {
      this._stats.fps = this._stats._n / this._stats._acc;
      this._stats.ms = (this._stats._acc / this._stats._n) * 1000;
      this._stats._acc = 0;
      this._stats._n = 0;
    }
    this.renderer.info.reset();
  }

  render() {
    if (this.composer) {
      this.composer.render();
    } else {
      this.renderer.clear();
      this.renderer.render(this.scene, this.camera);
      this.renderer.clearDepth();
      this.renderer.render(this.viewScene, this.viewCamera);
    }
  }

  get stats() {
    return this._stats;
  }

  /**
   * Turn shadow casting on or off wholesale.
   *
   * This is the single largest lever there is on a phone, and it is not a subtle
   * one. Measured on the built game: shadows account for **57% of every triangle
   * drawn** (1,261,988 with, 538,820 without) and a third of the draw calls
   * (208 -> 139), because each cascade re-renders the casters from the sun's point of
   * view. Render resolution, which is what the quality selector used to control by
   * itself, cannot touch any of that — it is geometry cost, not fragment cost.
   *
   * Materials must be recompiled, because whether a shader samples a shadow map is
   * baked into it. That is a real hitch of a few hundred milliseconds, which is why
   * this is a settings action and not something to call per frame.
   */
  setShadowsEnabled(on) {
    const want = !!on;
    if (this.renderer.shadowMap.enabled === want) return;
    this.renderer.shadowMap.enabled = want;
    const refresh = (obj) => {
      const m = obj.material;
      if (!m) return;
      if (Array.isArray(m)) for (const x of m) x.needsUpdate = true;
      else m.needsUpdate = true;
    };
    this.scene.traverse(refresh);
    this.viewScene.traverse(refresh);
  }
}

/**
 * True for a phone or tablet — a device driven by fingers on a small, dense screen.
 *
 * Deliberately not a user-agent sniff. What we actually need to know is "does this
 * device have a touchscreen and no mouse", and `pointer: coarse` plus `hover: none`
 * asks exactly that. A laptop with a touchscreen reports `hover: hover` and stays on
 * the desktop path, which is right — it has a trackpad and a desktop GPU.
 */
export function isTouchDevice() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return (
    window.matchMedia('(pointer: coarse)').matches &&
    window.matchMedia('(hover: none)').matches &&
    (navigator.maxTouchPoints ?? 0) > 0
  );
}

/**
 * Picks quality settings from the device. Users can override in the pause menu.
 *
 * The core/memory heuristic below is a desktop heuristic and it cannot be applied to
 * a phone: a current iPhone reports 8 cores and 8 GB, which is the *exact* signature
 * this function used to read as `ultra`. It would then be handed motion blur,
 * volumetrics, three shadow cascades, 2048px shadow maps and 4000 particles on a
 * tile-based mobile GPU behind a 3x device pixel ratio. Phones therefore get their
 * own tier before the CPU heuristic is consulted at all.
 */
export function detectQuality() {
  const dpr = window.devicePixelRatio || 1;
  const mem = navigator.deviceMemory ?? 8;
  const cores = navigator.hardwareConcurrency ?? 8;

  if (isTouchDevice()) {
    // Render resolution is the single biggest lever on a mobile GPU and the one the
    // player is least likely to notice: a phone's pixels are far below the size the
    // eye resolves at arm's length, so 1.25x of CSS pixels still looks sharp while
    // costing well under half the fragments of the panel's native 3x.
    const strong = cores >= 6 && mem >= 4;
    return {
      /**
       * Stays one of the three names every consumer already branches on. A fourth
       * value here would be silently dangerous rather than merely unknown: four
       * separate call sites are written as `!== 'medium'` or as a medium/high/else
       * ladder, so a phone reporting `'mobile'` would fall through to the *heaviest*
       * branch in the particle system, the decal atlas, the dynamic light budget and
       * the AI director. The device class is a separate question, so it gets a
       * separate field.
       */
      level: 'medium',
      mobile: true,
      maxPixelRatio: Math.min(dpr, strong ? 1.25 : 1),
      shadowMapSize: strong ? 1024 : 768,
      // Two cascades, not three. A phone screen is small enough that the third
      // cascade's ground is a handful of pixels wide.
      cascades: 2,
      ssao: false,
      bloom: true,
      motionBlur: false,
      volumetrics: false,
      maxDecals: strong ? 48 : 24,
      maxParticles: strong ? 700 : 400,
    };
  }

  let level = 'high';
  if (cores >= 8 && mem >= 8) level = 'ultra';
  if (cores <= 4 || mem <= 4) level = 'medium';

  return {
    level,
    mobile: false,
    maxPixelRatio: level === 'ultra' ? Math.min(dpr, 2) : level === 'high' ? Math.min(dpr, 1.5) : 1,
    shadowMapSize: level === 'ultra' ? 2048 : level === 'high' ? 1536 : 1024,
    cascades: level === 'medium' ? 2 : 3,
    ssao: level !== 'medium',
    bloom: true,
    motionBlur: level === 'ultra',
    volumetrics: level === 'ultra',
    maxDecals: level === 'ultra' ? 256 : level === 'high' ? 160 : 80,
    maxParticles: level === 'ultra' ? 4000 : level === 'high' ? 2200 : 1000,
  };
}
